import {Fab, MenuItem, Select, Stack, Toolbar, Typography} from '@mui/material'
import {Breadcrumbs, FixedFab, GoSchoolAppBar, HomeBreadcrumb, LoadingPage, PageLayout} from '@goschool/components'
import {Trans, useTranslation} from 'react-i18next'
import AddIcon from '@mui/icons-material/Add'
import {CoursesGrid} from './components/CoursesGrid'
import type {PropsWithChildren, ReactNode} from 'react'
import {useMemo, useState} from 'react'
import {AuthButton, useUserContext} from '@goschool/auth'
import {CreateCourseDialog} from './components/CreateCourseDialog'
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'
import type {GoSchoolUser} from '@goschool/model'
import {useCanCreateCourse, useCustomizationConfig} from '@goschool/model'

export function CoursesPage() {
  const {roles, user, goSchoolUser, state} = useUserContext()
  const [creating, setCreating] = useState(false)
  const {ReceptionPage} = useCustomizationConfig()
  const {t} = useTranslation()

  const courses = useMemo(
    () => {
      if (goSchoolUser == null) {
        return []
      }
      const allCourses = goSchoolUser.courses ?? []
      return allCourses.filter(
        course =>
          course.parent?.parent?.path === goSchoolUser.organization?.path
      )
    },
    [goSchoolUser]
  )

  const organizationRef = useMemo(
    () => goSchoolUser?.organization,
    [goSchoolUser]
  )
  const canCreateCourse = useCanCreateCourse(organizationRef, user?.uid, roles)


  if (user === undefined || state === 'pending' || goSchoolUser === undefined) {
    return <LoadingPage/>
  }

  if (goSchoolUser == null || goSchoolUser.organization == null) {
    return <ReceptionPage/>
    // return <WarningPage icon={<LocationCityIcon/>} title={t('course:warnings.noOrganization.title')}>
    //   <Trans i18nKey="course:warnings.noOrganization.description"/>
    //   <Typography variant="caption" component="p" textAlign="center"><Link
    //     href="mailto:info@goschool.ai">info@goschool.ai</Link></Typography>
    // </WarningPage>
  }

  if (courses.length === 0) {
    if (roles.includes('instructor')) {
      return <>
        <WarningPage icon={<LibraryBooksIcon/>} title={t('course:warnings.noCoursesCreateOne.title')}>
          <Stack direction="column" alignItems="center">
            <OrgSelector user={goSchoolUser}/>
            {canCreateCourse
              ? <>
                <Trans i18nKey="course:warnings.noCoursesCreateOne.description"/>
                <Fab disabled={organizationRef == null} color="primary" sx={{marginLeft: '1rem'}}
                  onClick={() => setCreating(true)}><AddIcon/></Fab>
              </>
              : <Trans i18nKey="course:warnings.noCoursesCreateOne.noSubscription"/>}
          </Stack>
        </WarningPage>
        <CreateCourseDialog organizationRef={organizationRef} displayed={creating} hide={() => setCreating(false)}/></>
    } else if (roles.includes('student')) {
      return <WarningPage icon={<MeetingRoomIcon/>} title={t('course:warnings.noCoursesJoinOne.title')}>
        <Trans i18nKey="course:warnings.noCoursesJoinOne.description"/>
      </WarningPage>
    } else {
      return <ReceptionPage/>
    }
  }

  return <PageLayout fullScreen={false} breadcrumbs={[]}>
    <Typography variant="h4" component="h1" gutterBottom={true}><Trans i18nKey="course:catalog.title"/></Typography>
    <OrgSelector user={goSchoolUser}/>

    <CoursesGrid courseRefs={courses}/>
    {canCreateCourse &&
      <FixedFab disabled={organizationRef == null} onClick={() => setCreating(true)} color="primary" horizontal="right"
        vertical="bottom">
        <AddIcon/>
      </FixedFab>}
    <CreateCourseDialog organizationRef={organizationRef} displayed={creating} hide={() => setCreating(false)}/>
  </PageLayout>
}


function OrgSelector({user}: { user: GoSchoolUser }) {
  const {selectOrganization} = useUserContext()
  const {dedicatedOrganization} = useCustomizationConfig()
  if (user.organizations.length < 2 || dedicatedOrganization != null) {
    return null
  }
  return <Select
    value={user.organization.path}
    onChange={e => {
      const selectedOrg = user.organizations.find(org => org.ref.path === e.target.value)
      if (selectedOrg) {
        selectOrganization(selectedOrg.ref)
      }
    }}
    size="small"
    sx={{mb: 2, minWidth: 200}}
  >
    {user.organizations.map(org => <MenuItem key={org.ref.path} value={org.ref.path}>
      {org.name}
    </MenuItem>)}
  </Select>
}

export function WarningPage({icon, title, children}: PropsWithChildren<{ icon: ReactNode, title: string }>) {
  return <PageLayout fullScreen={true}>
    <GoSchoolAppBar position="static">
      <Toolbar variant="dense" disableGutters={true}>
        <Breadcrumbs separator="›">
          <HomeBreadcrumb/>
        </Breadcrumbs>
      </Toolbar>
      <AuthButton/>
    </GoSchoolAppBar>
    <Stack flexGrow={1} direction="column" alignItems="center" justifyContent="center">
      <Stack direction="row" gap={2} alignItems="center" justifyContent="center">
        {/*<WarningAvatar>{icon}</WarningAvatar>*/}
        <Typography variant="h3" component="h1">
          {title}
        </Typography>
      </Stack>
      <Typography variant="h5">{children}</Typography>
    </Stack>
  </PageLayout>
}
